import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { generatePageNumArray } from '@partner-facets/components/Pagination/Service';
import { isDesktopDevice } from '@core/components/utility';
import LastPageSeparator from '@react/shared-component/PaginationLists/SeparatorView';
import {
	PrevPageIcon,
	NextPageIcon,
} from '@partner-facets/assets/svg/pageIcon';
import WrapperIconsComponent from '@partner-facets/components/Pagination/PaginationIcons.View';
import whenPrevNextIconClicked from '@react/shared-component/PaginationLists/Service';
import { updateParamFromUrlString } from '@core/components/utility';


/**
 * Renders pagination number's html list
 * @param {object} props - props object
 * @param {number} props.totalPages - total number of pages
 * @param {string} props.ItemClassNames - class names to be applied to each page number
 * @param {string} props.separatorClassNames - class names to be applied to separator
 * @param {function} props.additionalActionOnClick - additional action to be called on click
 * @param {function} props.onPageChange - function to be called when page is changed
 * @param {number} props.injectCurrentPageNumber - current page number
 * @param {function} props.handleKeyUp - function to be called on key up
 * @param {object} props.restAttribute - rest of the attributes for pagination list
 * @returns {element} - pagination list
 */
function NumberedPaginationList({
	totalPages = 0,
	ItemClassNames = '',
	separatorClassNames = '',
	additionalActionOnClick,
	onPageChange,
	injectCurrentPageNumber = 0,
	handleKeyUp,
	paginationUrlParam,
	...restattribute
}) {
	const [currentPage, setCurrentPage] = useState(injectCurrentPageNumber);

	useEffect(() => {
		setCurrentPage(injectCurrentPageNumber);
	}, [injectCurrentPageNumber]);

	const isMobile = useMemo(() => !isDesktopDevice());

	/**
	 * Page number Array are generated differently for mobile and desktop
	 * That is why we are passing isMobile as a parameter to generatePageNumArray
	 * @returns {Array} - array of page numbers
	 */
	const pageNumbers = () =>
		generatePageNumArray(currentPage, totalPages, isMobile);

	// LOGIC: when total page is lesser or equal 1, don't render anything
	if (totalPages <= 1) return null;

	/**
	 * This function is called when user clicks on a page number
	 */
	const handleClick = (page) => {
		setCurrentPage(page);

		// set your own action here if you want to do something else after page changes
		onPageChange(page);
	};

	const renderPageNumbers = pageNumbers(totalPages).map((pageNum) => (
		<React.Fragment key={pageNum}>
			<LastPageSeparator
				isLast={pageNum === totalPages}
				page={pageNum}
				separatorClassNames={separatorClassNames}
			/>

			{pageNum === currentPage && (
				<span
					key={pageNum}
					className={ItemClassNames}
					onClick={() => handleClick(pageNum)}
					data-isdisabled={pageNum === currentPage} // used to disable current page
					data-iscurrentpage={pageNum === currentPage} // used to identify and style current page
					onKeyUp={handleKeyUp}
					role='button'
					tabIndex={0}
					{...restattribute}
				>
					{pageNum}
				</span>
			)}

			{pageNum !== currentPage && (
				<a
					href={updateParamFromUrlString(paginationUrlParam, pageNum)}
					key={pageNum}
					className={`${ItemClassNames} text-base`}
					onClick={(e) => {
						e.preventDefault();
						handleClick(pageNum)
					}}
					data-isdisabled={pageNum === currentPage} // used to disable current page
					data-iscurrentpage={pageNum === currentPage} // used to identify and style current page
					onKeyUp={handleKeyUp}
					role='button'
					tabIndex={0}
					{...restattribute}
				>
					{pageNum}
				</a>
			)}
		</React.Fragment>
	));

	return (
		<>
			{currentPage > 1 && (
				<WrapperIconsComponent
					icon={PrevPageIcon}
					targetPageNum={currentPage - 1}
					paginationUrlParam={paginationUrlParam}
					handleFunction={() => {
						onPageChange(
							whenPrevNextIconClicked('prev', currentPage, setCurrentPage),
						);
					}}
				/>
			)}
			{renderPageNumbers}
			{currentPage < totalPages && (
				<WrapperIconsComponent
					icon={NextPageIcon}
					targetPageNum={currentPage + 1}
					paginationUrlParam={paginationUrlParam}
					handleFunction={() => {
						onPageChange(
							whenPrevNextIconClicked('next', currentPage, setCurrentPage),
						);
					}}
				/>
			)}
		</>
	);
}

NumberedPaginationList.propTypes = {
	totalPages: PropTypes.number.isRequired,
	ItemClassNames: PropTypes.string,
	separatorClassNames: PropTypes.string,
	additionalActionOnClick: PropTypes.func,
	onPageChange: PropTypes.func,
	injectCurrentPageNumber: PropTypes.number.isRequired,
	restattribute: PropTypes.object,
	handleKeyUp: PropTypes.func,
};

// NumberedPaginationList.defaultProps = {
// 	ItemClassNames: '',
// 	separatorClassNames: '',
// 	additionalActionOnClick: () => {},
// 	onPageChange: () => {},
// 	restattribute: {},
// 	handleKeyUp: () => {},
// };

export default NumberedPaginationList;
